/* eslint-disable no-underscore-dangle */
import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import RelatedItemsCarouselEventTracker from '../../tracking/RelatedItemsCarouselEventTracker';
import CollectionCarousel from '../CollectionCarousel';
import './DiscoverSkusCollectionCarousel.module.scss';
import SubSourceType from '../../helpers/SubSourceType';
import { useProductDetailsPageContext } from '../../../../ProductDetailsPage/ProductDetailsPageContext/ProductDetailsPageContext';
import { shopPdpPath } from '../../../../../utils/urlUtils';

const trackCarouselSeenInViewport =
  ({
    collectionName,
    outfitId,
    pdpSkuId,
    externalProductId,
    referringSkuId,
    referringSkuInStock,
    relatedItemsSkuIds,
    source,
    subSource,
    treatment,
    gated,
    dispatch,
  }) =>
  inView => {
    if (inView) {
      if (!gated) {
        dispatch({
          type: 'RECOMMENDED_COLLECTIONS_CAROUSEL_VIEWED',
          payload: { carouselId: treatment },
        });
      } else {
        RelatedItemsCarouselEventTracker.trackCarouselSeenInViewport({
          collection: collectionName,
          outfitId,
          pdpSkuId,
          externalProductId,
          referringSkuId,
          referringSkuInStock,
          relatedItemsSkuIds,
          source,
          subSource,
          carouselId: treatment,
        });
      }
    }
  };
const trackCarouselScroll =
  ({
    collectionName,
    pdpSkuId,
    externalProductId,
    referringSkuInStock,
    source,
    treatment,
    gated,
    dispatch,
  }) =>
  (inView, itemSkuId) => {
    if (inView) {
      if (!gated) {
        dispatch({
          type: 'RECOMMENDED_COLLECTIONS_CAROUSEL_SCROLLED',
          payload: { carouselId: treatment },
        });
      } else {
        RelatedItemsCarouselEventTracker.trackCarouselScroll({
          collectionName,
          pdpSkuId,
          externalProductId,
          referringSkuInStock,
          relatedItemIsVisible: inView,
          relatedItemSkuId: itemSkuId,
          source,
          carouselId: treatment,
        });
      }
    }
  };
const trackCarouselEndCardView =
  ({
    collectionName,
    pdpSkuId,
    externalProductId,
    source,
    subSource,
    treatment,
    gated,
    dispatch,
  }) =>
  inView => {
    if (inView) {
      if (!gated) {
        dispatch({
          type: 'RECOMMENDED_COLLECTIONS_CAROUSEL_END_CARD_VIEWED',
          payload: { carouselId: treatment },
        });
      } else {
        RelatedItemsCarouselEventTracker.trackCarouselEndCardView({
          carouselId: treatment,
          collectionName,
          pdpSkuId,
          externalProductId,
          source,
          subSource,
        });
      }
    }
  };

// Mapping of `treatment` to our `sub-source` product tracking value. If we don't have a specific
// mapping here, we will default to the `treatment` value itself.
const treatmentSubSourceMap = {
  best_brands: SubSourceType.BRAND_ITEMS,
  similar_items: SubSourceType.SIMILAR_ITEMS,
};

/**
 * Collection Carousel displays a generic list of clothing items
 * which may potentially contain products or outfits
 */
const DiscoverSkusCollectionCarousel = ({
  content,
  wrapperStyle,
  minimumItemsToRender,
}) => {
  const carouselRef = useRef(null);
  const [state, dispatch] = useProductDetailsPageContext();
  const pdpSkuId = state.selectedSkuId || state.fallbackSkuId;
  const externalProductId = state.externalProductId || '';
  const referringSkuId = state.selectedSkuId || state.fallbackSkuId;
  const referringSkuInStock =
    state.priceAndInventoryData?.variantBySkuId?.inventory?.availability !==
    'OUT_OF_STOCK';
  const subSource = state.subSource || '';
  const source = state.source;
  const relatedItemsSkuIds =
    content?.shopProducts?.nodes?.map(node => node.variantBySkuId.id) || [];
  const items = content.shopProducts.nodes;

  if (!items.length) {
    return null;
  }

  const shopItemPath = ({ skuId, url }) => {
    const carouselSubSource =
      treatmentSubSourceMap[content.treatment] || content.treatment;

    const shopUrl = shopPdpPath(url, {
      source,
      referringSkuId: pdpSkuId,
      outfitId: state.outfitId,
      collection: state.collectionName,
      subSource: carouselSubSource,
      recommendedSkuId: skuId,
      queryId: state.queryId,
    }).replace('https://www.stitchfix.com', '');

    return shopUrl?.replace('product', state.urlScope);
  };

  const onCarouselVisible = trackCarouselSeenInViewport({
    collectionName: state.collectionName,
    pdpSkuId,
    externalProductId,
    outfitId: state.outfitId,
    referringSkuId,
    referringSkuInStock,
    relatedItemsSkuIds,
    source,
    subSource,
    treatment: content.treatment,
    gated: state.gated,
    dispatch,
  });

  const onItemVisible = trackCarouselScroll({
    collectionName: state.collectionName,
    pdpSkuId,
    externalProductId,
    outfitId: state.outfitId,
    referringSkuId,
    referringSkuInStock,
    relatedItemsSkuIds,
    source,
    subSource,
    treatment: content.treatment,
    gated: state.gated,
    dispatch,
  });

  const onEndCardVisible = trackCarouselEndCardView({
    collectionName: state.collectionName,
    pdpSkuId,
    externalProductId,
    source,
    subSource,
    treatment: content.treatment,
    gated: state.gated,
    dispatch,
  });

  const onItemClick = () => {
    if (!state.gated) {
      dispatch({
        type: 'RECOMMENDED_COLLECTIONS_CAROUSEL_ITEM_CLICKED',
        payload: { carouselId: content.treatment, itemId: pdpSkuId },
      });
    } else {
      RelatedItemsCarouselEventTracker.trackCarouselItemClick({
        collectionName: state.collectionName,
        relatedItemsSkuIds,
        pdpSkuId,
        externalProductId,
        source,
        subSource,
        treatment: content.treatment,
      });
    }
  };

  const onClickEndCard = () => {
    if (!state.gated) {
      dispatch({
        type: 'RECOMMENDED_COLLECTIONS_CAROUSEL_END_CARD_CLICKED',
        payload: { carouselId: content.treatment },
      });
    } else {
      RelatedItemsCarouselEventTracker.trackCarouselEndCardClick({
        carouselId: content.treatment,
        collectionName: state.collectionName,
        pdpSkuId,
        externalProductId,
        source,
        subSource,
      });
    }
  };

  const onClickViewAll = () => {
    if (!state.gated) {
      dispatch({
        type: 'RECOMMENDED_COLLECTIONS_CAROUSEL_VIEW_ALL_CLICKED',
        payload: { carouselId: content.treatment },
      });
    } else {
      RelatedItemsCarouselEventTracker.trackCarouselViewAllClick({
        carouselId: content.treatment,
        collectionName: state.collectionName,
        pdpSkuId,
        externalProductId,
        source,
        subSource,
      });
    }
  };

  if (items.length < minimumItemsToRender) {
    return null;
  }

  const collectionTrackingId = `discover-skus:${content.treatment}`;

  return (
    <div key={content.title} styleName={wrapperStyle}>
      <div styleName="inner-content">
        <div ref={carouselRef}>
          <CollectionCarousel
            id={content.id}
            collectionTrackingId={collectionTrackingId}
            treatment={content.treatment}
            brandCategoryLink={
              state.shopProduct?.shopBrandCategoryLink?.destinationPath || ''
            }
            items={items}
            title={content.title}
            onItemClick={onItemClick}
            onCarouselVisible={onCarouselVisible}
            onItemVisible={onItemVisible}
            onClickEndCard={onClickEndCard}
            onClickViewAll={onClickViewAll}
            onEndCardVisible={onEndCardVisible}
            shopItemPath={shopItemPath}
          />
        </div>
      </div>
    </div>
  );
};

DiscoverSkusCollectionCarousel.propTypes = {
  content: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    treatment: PropTypes.string.isRequired,
    shopProducts: PropTypes.shape({
      nodes: PropTypes.arrayOf(PropTypes.shape({})),
    }).isRequired,
  }).isRequired,
  wrapperStyle: PropTypes.string,
  minimumItemsToRender: PropTypes.number,
};

DiscoverSkusCollectionCarousel.defaultProps = {
  wrapperStyle: '',
  minimumItemsToRender: 3,
};

export default DiscoverSkusCollectionCarousel;
